import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import NursingRecordForm from "../NursingRecord/NursingRecordForm";
import {
    fetchNursingRecord,
    updateNursingRecord,
    fetchNurses, // ✅ Naya Thunk
} from "../../../store/slices/nursingRecordsSlice";

const EditNursingRecord = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { isLoading, error, nurses } = useSelector(state => state.nursingRecords);
    const { user } = useSelector(state => state.auth);

    const [formData, setFormData] = useState({

        name: "",
        mrNo: "",
        roomNo: "",
        consultant: "",
    });
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(fetchNurses());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchNursingRecord(id))
            .unwrap()
            .then((data) => {
                setFormData({
                    patientId: patientId || data.patientId,
                    caseId: caseId || data.caseId,
                    name: data.name || "",
                    mrNo: data.mr_no || "",
                    roomNo: data.room_no || "",
                    consultant: data.consultant || "",
                });

                const rawRows = Array.isArray(data.rows) ? data.rows.slice() : [];
                rawRows.sort((a, b) => {
                    const aTime = a.created_at ? new Date(a.created_at).getTime() : a.id;
                    const bTime = b.created_at ? new Date(b.created_at).getTime() : b.id;
                    return bTime - aTime;
                });

                setRows(
                    rawRows.length > 0
                        ? rawRows.map((row) => ({
                            id: row.id,
                            date: row.date,
                            hours: row.hours,
                            remarks: row.remarks,
                            nurse: row.nurse,
                            nurse_id: row.nurse_id,
                            created_at: row.created_at,
                        }))
                        : [
                            { id: Date.now(), date: "", hours: "", remarks: "", nurse: "", nurse_id: null },
                        ]
                );
            })
            .catch((err) => {
                Swal.fire({
                    title: "Error",
                    text: err?.message || "Failed to fetch Nursing Record.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            });
    }, [dispatch, id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || rows.length === 0) {
            setErrors({
                name: !formData.name ? "Name is required." : "",
                nursing_records: rows.length === 0 ? "At least one nursing record is required." : "",
            });
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields before updating.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        const payload = {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            name: formData.name,
            mr_no: formData.mrNo,
            room_no: formData.roomNo,
            consultant: formData.consultant,
            nursing_records: rows.map((row) => ({
                id: row.id,
                date: row.date,
                hours: row.hours,
                remarks: row.remarks,
                nurse: row.nurse,
                nurse_id: row.nurse_id,
            })),
        };

        try {
            await dispatch(updateNursingRecord({ case_id: caseId, recordData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Nursing Record updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: err?.message || "Failed to update Nursing Record.",
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    if (isLoading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/nursing-records/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        {error && (
                            <div className="alert alert-danger">
                                <strong>Error:</strong> {error}
                            </div>
                        )}
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors)
                                    .filter(Boolean)
                                    .map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <NursingRecordForm
                                formData={formData}
                                handleChange={handleChange}
                                rows={rows}
                                setRows={setRows}
                                nurses={nurses}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-edit-2-line me-1"></i> Update
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditNursingRecord;
